import dynamic from 'next/dynamic';
import { useEffect, FC } from 'react';
import If from 'global/ErrorHandlers/If';
import { CancelOrderParams } from 'global/Securities/types/api/orderDetail';
import useGenPortfolio from 'global/Securities/websocket/usePortfolio';
import tradingDataStore from 'lib/stores/tradingData';
import useTradingAccount from 'hooks/useTradingAccount';
import PortoDetailModal from 'features/trade/components/Portfolio/PortoDetailModal';
import OrderDetailModal from 'features/trade/components/OrderTable/OrderDetailModal';
import { OrderStateItem } from 'global/Securities/types/store/order';
import shallow from 'zustand/shallow';
import tradingModalStore from 'global/TradingModal/store';
import useTradingStore from 'features/transaction/useTradingStore';
import { MODAL_TYPE } from 'global/TradingModal/RealTradingModal/types';
import CancelModal from './CancelModal';
import { PortoData } from '../types';

const ModalTransactionSecurities = dynamic(
  () => import('global/TradingModal'),
  {
    ssr: false,
  },
);

const ModalShareTradeManager = dynamic(
  () => import('features/sharetrade/components/ModalShareTradeManager'),
  { ssr: false },
);

type TradingModalProps = {
  portoData: PortoData | null;
  orderData: OrderStateItem | null;
  cancelData: CancelOrderParams | null;
  onClose: () => void;
  setCancelData: (data: CancelOrderParams | null) => void;
  setPortoData: (data: PortoData | null) => void;
};

const ChartbitTradingModals: FC<TradingModalProps> = ({
  portoData,
  cancelData,
  orderData,
  onClose,
  setCancelData,
  setPortoData,
}) => {
  const { isRealTrading } = useTradingAccount();

  const { getPortfolio, getOrderHistory } = tradingDataStore((state) => ({
    getPortfolio: state.getPortfolio,
    getOrderHistory: state.getOrderHistory,
  }));

  useGenPortfolio(true);

  const modal = useTradingStore((s) => s.openModal);
  const setModal = useTradingStore((s) => s.setModal);
  const modalData = tradingModalStore((s) => s.modalData, shallow);

  useEffect(() => {
    if (isRealTrading || modal === MODAL_TYPE.TRANSACTION) {
      getPortfolio(isRealTrading, false);
      getOrderHistory(isRealTrading, false);
    }
  }, [isRealTrading, modal]);

  useEffect(() => {
    if (modalData?.showShariaRestrictionBuy) {
      setModal(MODAL_TYPE.SHARIA_UNAVAILABLE);
    }
  }, [modalData]);

  useEffect(() => {
    if (modal === MODAL_TYPE.ORDER_PLACED) {
      setPortoData(null);
    }
  }, [modal]);

  return (
    <>
      <If condition={Boolean(cancelData)}>
        <CancelModal
          visible={Boolean(cancelData?.symbol)}
          data={cancelData}
          onCancel={onClose}
        />
      </If>
      <If condition={Boolean(portoData)}>
        <PortoDetailModal
          visible={Boolean(portoData?.symbol)}
          {...portoData}
          onCancel={onClose}
        />
      </If>

      <If condition={Boolean(orderData) && !cancelData?.symbol}>
        <OrderDetailModal
          visible={Boolean(orderData?.symbol)}
          data={orderData}
          onCancel={onClose}
          onCancelOrder={() => {
            if (orderData) {
              setCancelData({
                orderId: orderData.orderid,
                symbol: orderData.symbol,
              });
            }
          }}
        />
      </If>

      <ModalTransactionSecurities />
      <ModalShareTradeManager />
    </>
  );
};

export default ChartbitTradingModals;
